import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Lock, Loader2, ShieldCheck, ArrowLeft } from "lucide-react";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const inputCls =
  "w-full bg-white border border-slate-300 rounded-xl px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-800/40 focus:border-emerald-800 transition";

const AdminLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(
        `${API}/auth/login`,
        { email: email.trim(), password },
        { withCredentials: true }
      );
      navigate("/admin/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO title="Clinic Login | Delhi STD Clinic" description="Private clinic access." path="/admin" />
      <section className="min-h-[80vh] flex items-center justify-center bg-[#FAF9F6] texture-grain pt-28 pb-16 px-5" data-testid="admin-login-page">
        <div className="w-full max-w-md">
          <div className="bg-white border border-slate-900/10 rounded-3xl p-8 sm:p-10 shadow-[0_20px_60px_-24px_rgba(15,23,42,0.18)]">
            <div className="text-center">
              <img src="/images/logo.jpeg" alt="Delhi STD Clinic" className="h-20 w-auto mx-auto mix-blend-multiply" />
              <h1 className="mt-4 font-serif text-3xl font-semibold text-slate-950">Clinic Login</h1>
              <p className="mt-2 text-sm text-slate-500 inline-flex items-center gap-1.5">
                <Lock className="w-3.5 h-3.5" /> Private access — authorized staff only
              </p>
            </div>

            <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-login-form">
              <div>
                <label htmlFor="admin-email" className="block text-sm font-medium text-slate-800 mb-1.5">Email</label>
                <input
                  id="admin-email"
                  data-testid="admin-email-input"
                  type="email"
                  required
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Clinic email"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="admin-password" className="block text-sm font-medium text-slate-800 mb-1.5">Password</label>
                <input
                  id="admin-password"
                  data-testid="admin-password-input"
                  type="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className={inputCls}
                />
              </div>

              {error && (
                <p data-testid="admin-login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="admin-login-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-emerald-900 hover:bg-emerald-800 disabled:opacity-60 text-white font-semibold text-sm px-8 py-3.5 rounded-full transition-all shadow-md"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Signing in…</>
                ) : (
                  <><ShieldCheck className="w-4 h-4" /> Sign In</>
                )}
              </button>
            </form>
          </div>
          <div className="text-center mt-6">
            <Link to="/" data-testid="admin-back-home" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-emerald-900 transition-colors">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to website
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default AdminLogin;
